/**
 * FramrManager - Core framing and white-labeling logic
 * 
 * CHANGE HISTORY:
 * - 251025_1651 [pending] danielbeer: Initial creation
 * 
 * Platform-agnostic core for app framing and white-labeling.
 * Manages configuration, branding, and context provision.
 */

import type { FrameConfig } from './types.js';

/**
 * FramrManager class
 */
export class FramrManager {
  private config: FrameConfig;
  private listeners: Set<(config: FrameConfig) => void>;

  constructor(initialConfig: FrameConfig = {}) {
    this.config = this.normalizeConfig(initialConfig);
    this.listeners = new Set();
  }

  /**
   * Normalize and validate configuration
   */
  private normalizeConfig(config: FrameConfig): FrameConfig {
    return {
      // Defaults
      appName: 'OpCentral',
      theme: 'auto',
      layout: 'default',
      showHeader: true,
      showFooter: true,
      showSidebar: false,
      permissions: [],
      
      // Override with provided config
      ...config,
      
      // Merge nested objects
      orgColors: {
        ...config.orgColors,
      },
      features: {
        ...config.features,
      },
      context: {
        ...config.context,
      },
      metadata: {
        ...config.metadata,
      },
    };
  }

  /**
   * Get current configuration
   */
  getConfig(): FrameConfig {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<FrameConfig>): void {
    this.config = this.normalizeConfig({
      ...this.config,
      ...updates,
    });
    
    this.notifyListeners();
  }

  /**
   * Check if app is white-labeled
   */
  isWhiteLabeled(): boolean {
    return !!(
      this.config.orgId ||
      this.config.customDomain ||
      this.config.orgLogo ||
      this.config.appLogo
    );
  }

  /**
   * Get organization ID
   */
  getOrgId(): string | undefined {
    return this.config.orgId;
  }

  /**
   * Get app name (white-labeled or default)
   */
  getAppName(): string {
    return this.config.appName || 'OpCentral';
  }

  /**
   * Get theme configuration
   */
  getTheme(): string {
    return this.config.theme || 'auto';
  }

  /**
   * Check if feature is enabled
   */
  isFeatureEnabled(feature: string): boolean {
    return this.config.features?.[feature] ?? false;
  }

  /**
   * Enable/disable feature
   */
  setFeature(feature: string, enabled: boolean): void {
    this.updateConfig({
      features: {
        ...this.config.features,
        [feature]: enabled,
      },
    });
  }

  /**
   * Get context value
   */
  getContext(key: string): any {
    return this.config.context?.[key];
  }

  /**
   * Set context value
   */
  setContext(key: string, value: any): void {
    this.updateConfig({
      context: {
        ...this.config.context,
        [key]: value,
      },
    });
  }

  /**
   * Check permission
   */
  hasPermission(permission: string): boolean {
    return this.config.permissions?.includes(permission) ?? false;
  }

  /**
   * Add permission
   */
  addPermission(permission: string): void {
    if (!this.hasPermission(permission)) {
      this.updateConfig({
        permissions: [...(this.config.permissions || []), permission],
      });
    }
  }

  /**
   * Remove permission
   */
  removePermission(permission: string): void {
    this.updateConfig({
      permissions: (this.config.permissions || []).filter(p => p !== permission),
    });
  }

  /**
   * Get metadata value
   */
  getMetadata(key: string): any {
    return this.config.metadata?.[key];
  }

  /**
   * Set metadata value
   */
  setMetadata(key: string, value: any): void {
    this.updateConfig({
      metadata: {
        ...this.config.metadata,
        [key]: value,
      },
    });
  }

  /**
   * Subscribe to configuration changes
   */
  subscribe(listener: (config: FrameConfig) => void): () => void {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of configuration change
   */
  private notifyListeners(): void {
    const config = this.getConfig();
    this.listeners.forEach(listener => listener(config));
  }

  /**
   * Export configuration (for persistence)
   */
  export(): string {
    return JSON.stringify(this.config, null, 2);
  }

  /**
   * Import configuration (from persistence)
   */
  import(json: string): void {
    try {
      const config = JSON.parse(json);
      this.updateConfig(config);
    } catch (error) {
      throw new Error(`Failed to import configuration: ${error}`);
    }
  }

  /**
   * Reset to defaults
   */
  reset(): void {
    this.config = this.normalizeConfig({});
    this.notifyListeners();
  }
}
